<script setup lang="ts">
import { Clock3 } from 'lucide-vue-next';
import DetailsSheet from '@/components/editing/DetailsSheet.vue';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import type { TimeEntry } from '~/types/models';

type ContextOption = { id: string; label: string; projectId?: string; projectName?: string; clientId?: string; clientName?: string };

const props = withDefaults(defineProps<{
    open: boolean;
    mode?: 'timer' | 'manual' | 'edit';
    entry?: TimeEntry | null;
    taskOptions?: ContextOption[];
    projectOptions?: ContextOption[];
    clientOptions?: ContextOption[];
    loading?: boolean;
    title?: string;
    description?: string;
    submitLabel?: string;
}>(), {
    mode: 'manual',
    entry: null,
    taskOptions: () => [],
    projectOptions: () => [],
    clientOptions: () => [],
    loading: false,
    title: '',
    description: '',
    submitLabel: '',
});

const emit = defineEmits<{
    'update:open': [value: boolean];
    submit: [value: {
        taskId?: string;
        taskTitle?: string;
        projectId?: string;
        projectName?: string;
        clientId?: string;
        clientName?: string;
        description?: string;
        billable: boolean;
        startedAt?: Date;
        endedAt?: Date;
    }];
}>();

const taskId = ref('');
const projectId = ref('');
const clientId = ref('');
const notes = ref('');
const billable = ref(false);
const startedAt = ref('');
const endedAt = ref('');
const initialState = ref('');

const sheetTitle = computed(() => props.title || (props.mode === 'timer' ? 'Start a timer' : props.mode === 'edit' ? 'Edit time entry' : 'Add time entry'));
const sheetDescription = computed(() => props.description || (props.mode === 'timer'
    ? 'Choose what you are working on, or start general workspace time.'
    : 'Record time against a task, project, or client.'));
const resolvedSubmitLabel = computed(() => props.submitLabel || (props.mode === 'timer' ? 'Start timer' : props.mode === 'edit' ? 'Save changes' : 'Add entry'));
const taskMenuOptions = computed(() => props.taskOptions.map(option => ({ value: option.id, label: option.label })));
const projectMenuOptions = computed(() => props.projectOptions.map(option => ({ value: option.id, label: option.label })));
const clientMenuOptions = computed(() => props.clientOptions.map(option => ({ value: option.id, label: option.label })));
const formState = computed(() => JSON.stringify({
    taskId: taskId.value,
    projectId: projectId.value,
    clientId: clientId.value,
    notes: notes.value,
    billable: billable.value,
    startedAt: startedAt.value,
    endedAt: endedAt.value,
}));
const dirty = computed(() => formState.value !== initialState.value);
const saveDisabled = computed(() => {
    if (props.mode === 'timer') return false;
    if (!startedAt.value || !endedAt.value) return true;
    return new Date(endedAt.value).getTime() <= new Date(startedAt.value).getTime();
});

function toLocalDateTime(value: unknown): string {
    if (!value) return '';
    const date = value instanceof Date
        ? value
        : typeof value === 'object' && value !== null && 'toDate' in value
            ? (value as { toDate: () => Date }).toDate()
            : new Date(String(value));
    if (Number.isNaN(date.getTime())) return '';
    const offset = date.getTimezoneOffset() * 60_000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function resetForm() {
    taskId.value = props.entry?.taskId || '';
    projectId.value = props.entry?.projectId || '';
    clientId.value = props.entry?.clientId || '';
    notes.value = props.entry?.description || '';
    billable.value = props.entry?.billable === true;
    const end = props.entry?.endedAt || new Date();
    const endDate = end instanceof Date ? end : new Date();
    const start = props.entry?.startedAt || new Date(endDate.getTime() - 60 * 60_000);
    startedAt.value = toLocalDateTime(start);
    endedAt.value = toLocalDateTime(end);
    nextTick(() => { initialState.value = formState.value; });
}

watch(() => [props.open, props.entry?.id, props.mode], ([open]) => {
    if (open) resetForm();
}, { immediate: true });

watch(taskId, (value) => {
    const task = props.taskOptions.find(option => option.id === value);
    if (!task) return;
    if (task.projectId) projectId.value = task.projectId;
    if (task.clientId) clientId.value = task.clientId;
});

watch(projectId, (value) => {
    const project = props.projectOptions.find(option => option.id === value);
    if (project?.clientId) clientId.value = project.clientId;
});

function submit() {
    const task = props.taskOptions.find(option => option.id === taskId.value);
    const project = props.projectOptions.find(option => option.id === projectId.value);
    const client = props.clientOptions.find(option => option.id === clientId.value);
    emit('submit', {
        taskId: task?.id,
        taskTitle: task?.label,
        projectId: project?.id,
        projectName: project?.label,
        clientId: client?.id,
        clientName: client?.label,
        description: notes.value.trim() || undefined,
        billable: billable.value,
        ...(props.mode === 'timer' ? {} : {
            startedAt: new Date(startedAt.value),
            endedAt: new Date(endedAt.value),
        }),
    });
}
</script>

<template>
    <DetailsSheet
        :open="open"
        :title="sheetTitle"
        :description="sheetDescription"
        :dirty="dirty"
        :saving="loading"
        :save-label="resolvedSubmitLabel"
        :saving-label="mode === 'timer' ? 'Starting…' : 'Saving…'"
        :save-disabled="saveDisabled"
        :require-dirty-to-save="mode !== 'timer'"
        @update:open="emit('update:open', $event)"
        @save="submit"
    >
        <div class="space-y-6">
            <div class="flex items-center gap-3 border-b border-border/60 pb-5">
                <div class="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><Clock3 class="size-4" /></div>
                <p class="text-sm leading-6 text-muted-foreground">Linking time is optional. Use the context that will make reporting useful later.</p>
            </div>

            <div class="space-y-2">
                <Label>Task</Label>
                <SearchableSelectMenu v-model="taskId" :options="taskMenuOptions" placeholder="No linked task" search-placeholder="Search tasks..." allow-clear clear-label="No task" show-all-by-default variant="form" />
            </div>
            <div class="grid gap-4 sm:grid-cols-2">
                <div class="min-w-0 space-y-2">
                    <Label>Project</Label>
                    <SearchableSelectMenu v-model="projectId" :options="projectMenuOptions" placeholder="No linked project" search-placeholder="Search projects..." allow-clear clear-label="No project" show-all-by-default variant="form" />
                </div>
                <div class="min-w-0 space-y-2">
                    <Label>Client</Label>
                    <SearchableSelectMenu v-model="clientId" :options="clientMenuOptions" placeholder="No linked client" search-placeholder="Search clients..." allow-clear clear-label="No client" show-all-by-default variant="form" />
                </div>
            </div>
            <div v-if="mode !== 'timer'" class="grid gap-4 sm:grid-cols-2">
                <div class="space-y-2"><Label for="time-start">Start</Label><Input id="time-start" v-model="startedAt" type="datetime-local" /></div>
                <div class="space-y-2"><Label for="time-end">End</Label><Input id="time-end" v-model="endedAt" type="datetime-local" /></div>
            </div>
            <div class="space-y-2"><Label for="time-notes">Notes</Label><Input id="time-notes" v-model="notes" placeholder="What are you working on?" /></div>
            <div class="flex items-center justify-between gap-4 border-t border-border/60 pt-5">
                <div><p class="text-sm font-medium">Billable time</p><p class="text-xs leading-5 text-muted-foreground">Include this entry in billable reporting.</p></div>
                <Switch v-model:checked="billable" />
            </div>
        </div>
    </DetailsSheet>
</template>
